/**
 * Tile + object contract shared between the game and the Tiled maps.
 *
 * The placeholder tileset (public/assets/tilesets/placeholder-tiles.png) is an
 * 8x1 grid of 32px tiles. Tiled paints with GIDs, where GID = localId + firstgid
 * and firstgid is 1, so GID = localId + 1. "Empty" map cells are GID 0.
 *
 * IMPORTANT: tools/gen-placeholder-assets.mjs hardcodes the same indices/colors.
 * If you change one, change the other. Once the art phase produces a real
 * tileset drawn in Tiled, this file stays the source of truth for *class names*
 * and *custom properties*; the raw GID numbers stop mattering because collision
 * and behaviour are driven by tile properties, not by specific ids.
 */

/** Name of the tileset as it appears in Tiled (the `name` field of the tileset). */
export const TILESET_NAME = "placeholder";

/** Phaser cache key we load the tileset image under. */
export const TILESET_KEY = "tiles";

/** Second cache key: the same tileset loaded as a 32px spritesheet, so decor
 *  objects (tile objects placed in Tiled, i.e. objects with a `gid`) can render
 *  an individual tile frame. */
export const TILESET_SHEET_KEY = "tiles-sheet";

/** Where image-layer images live under public/. Tiled writes whatever relative
 *  path it likes (often into ~/Downloads); at runtime we ignore that and load by
 *  basename from here, so the only requirement is that the file exists here. */
export const IMAGE_DIR = "assets/images/";

/** Basename of a Tiled image path, tolerant of / and \ separators. */
export function imageBasename(path: string): string {
  return path.split(/[\\/]/).pop() || path;
}

/** Phaser texture key for a Tiled image-layer image (stable across the path
 *  Tiled happens to store). */
export function imageLayerKey(path: string): string {
  return "img-" + imageBasename(path).replace(/\.[^.]+$/, "");
}

/** Runtime URL for a Tiled image-layer image (under public/assets/images/). */
export function imageLayerUrl(path: string): string {
  return IMAGE_DIR + imageBasename(path);
}

/** Local tile ids in the placeholder tileset (0-based, matches image columns). */
export const Tile = {
  GROUND: 0, // green - top walking surface
  DIRT: 1, // brown - solid fill under ground
  WALL: 2, // gray - solid brick / boundary
  PLATFORM: 3, // tan - solid ledge
  HAZARD: 4, // red - spikes, damages the player
  LADDER: 5, // yellow - climbable, non-solid
  GOAL: 6, // magenta - visual marker for an exit
  MIDFLOOR: 7, // LAST tile - solid, but anchors the player on the tile's vertical MIDDLE
  //             (feet on the mid-line) so a floor can be nudged half a tile down to line
  //             up with backdrop art. See src/ui/midFloor.ts.
} as const;

/**
 * Object "classes" (Tiled's object type/class field). We also set each object's
 * `name` to the same string so the game can discriminate on `name` regardless
 * of whether a given Tiled/Phaser version surfaces `type` or `class`.
 */
export const Obj = {
  SPAWN: "spawn",
  COLLECTIBLE: "collectible",
  SHEEPY: "sheepy",
  THROWABLE_PICKUP: "throwable_pickup",
  ELEVATOR: "elevator", // moving platform that carries the player between floors
  UPGRADE: "upgrade", // Level 1 (Dan) worn accessory pickup; which one via the `acc` prop
  EXIT: "exit",
  // Level 2 (Lucas) puzzle pieces
  BOX: "box",
  KEY: "key",
  DOOR: "door",
  SWITCH: "switch",
  PLATE: "plate", // pressure plate: opens its door while/after a box rests on it
  VAULT: "vault",
  // Level 3 (Andrew) speed-run pieces
  BUG: "bug",
} as const;

/** Custom-property names we read off Tiled objects/tiles. */
export const Prop = {
  COLLIDES: "collides",
  ONEWAY: "oneway", // one-way / jump-through platform: solid only when landing from above
  HAZARD: "hazard",
  LADDER: "ladder",
  MIDFLOOR: "midfloor", // solid, but anchors the player on the tile's vertical middle

  KIND: "kind", // collectible kind: money | laptop | vw | document | feature
  VALUE: "value", // collectible point value
  TIER: "tier", // throwable tier: 1..4 (phone/email/slack, or code/claude)
  SIZE: "size", // bug size: small | big
  ACC: "acc", // upgrade accessory: headphones | watch (Level 1 `upgrade` objects)
  MILESTONE: "milestone", // exit milestone text
  RISE: "rise", // elevator travel distance, in tiles (upward)
  SPEED: "speed", // elevator speed, px/sec (optional)
  OPENS_WITH: "opensWith", // door token: a key kind, "switch", or "vault"
  BASE: "base", // Lucas output-cache base value (paid out × its zone's multiple)
  ZONE: "zone", // Lucas cache zone (a lever boosts a whole zone)
  ID: "id", // Lucas lever/barrier identifier
  BOOST: "boost", // Lucas lever: zone whose multiple it raises
  TO: "to", // Lucas lever: multiple it raises the boosted zone to
  SEAL: "seal", // Lucas lever: barrier id it seals shut
  OPEN: "open", // Lucas lever: barrier id it opens
  LABEL: "label", // Lucas lever: on-screen effect text (\n for line breaks)
  START: "start", // Lucas barrier: initial state, "open" | "sealed"
  // Map-level custom properties (set on the Map, not on an object), so a level's
  // identity can be authored in Tiled instead of hardcoded in the scene.
  TITLE: "title", // overrides the name shown top-left in the HUD
  HINT: "hint", // right-side HUD hint line (dynamic values still come from code)
} as const;
